import { heicTo, isHeic } from "heic-to"

type TTargetImageType = "image/jpeg"

type TImageProcessorDimensions = { width: number; height: number }

export const TImageProcessorErrorKeys = {
  internal_error: "internal_error",
  unknown_error: "unknown_error",
} as const

export async function convertHEICFile({
  file,
  targetImageType,
}: {
  file: File
  targetImageType: TTargetImageType
}): Promise<File> {
  const convertedFile = await heicTo({
    blob: file,
    type: targetImageType,
    quality: 0.8,
  })

  const [fileName, _] = file.name.split(".")
  const newFileName = `${fileName}.jpg`

  return new File([convertedFile], newFileName, { type: targetImageType })
}

function canvasToBlob(
  canvas: HTMLCanvasElement,
  type: string,
  quality: number
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob)
        }

        reject(new Error(TImageProcessorErrorKeys.internal_error))
      },
      type,
      quality
    )
  })
}

export async function processFile({
  file,
  targetImageType,
  quality,
  maxWidth,
  maxHeight,
  maxSize,
}: {
  file: File
  targetImageType: TTargetImageType
  quality: number
  maxWidth: number
  maxHeight: number
  maxSize: number
}): Promise<{ file: File; dimensions: TImageProcessorDimensions }> {
  let convertedFile = file
  if (await isHeic(file)) {
    convertedFile = await convertHEICFile({ file, targetImageType })
  }

  return new Promise((resolve, reject) => {
    const image = new Image()
    image.src = URL.createObjectURL(convertedFile)

    image.onload = async () => {
      const canvas = document.createElement("canvas")
      const context = canvas.getContext("2d")

      if (!context) {
        throw new Error(TImageProcessorErrorKeys.internal_error)
      }

      const ratio = Math.min(
        1,
        maxWidth / image.width,
        maxHeight / image.height
      )

      const width = image.width * ratio
      const height = image.height * ratio

      canvas.width = width
      canvas.height = height

      context.drawImage(image, 0, 0, width, height)

      const targetQuality = file.size > maxSize ? quality : 1

      const blob = await canvasToBlob(canvas, targetImageType, targetQuality)

      resolve({
        file: new File([blob], file.name, { type: targetImageType }),
        dimensions: { width, height },
      })
    }

    image.onerror = () => {
      reject(new Error(TImageProcessorErrorKeys.internal_error))
    }
  })
}
